import React, { useState, useEffect } from 'react';
import { Shield, Key, ScrollText, Monitor, CheckCircle2, XCircle, AlertTriangle, RefreshCw, Loader2, Trash2, Smartphone, Laptop, Globe } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import api from '../../lib/api';

const TABS = [
  { id: 'overview', label: 'Overview', icon: Shield },
  { id: 'sso', label: 'SSO / SAML', icon: Key },
  { id: 'audit', label: 'Audit Logs', icon: ScrollText },
  { id: 'sessions', label: 'Sessions', icon: Monitor },
];

export default function SecurityCompliance() {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div className="space-y-6" data-testid="security-compliance-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Security & Compliance</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage SSO, audit logs, session policies, and compliance readiness</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 p-1 bg-muted/50 rounded-lg w-fit" data-testid="security-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
              activeTab === tab.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="h-3.5 w-3.5" />{tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && <ComplianceOverview />}
      {activeTab === 'sso' && <SSOConfig />}
      {activeTab === 'audit' && <AuditLogs />}
      {activeTab === 'sessions' && <SessionManagement />}
    </div>
  );
}

function ComplianceOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadOverview(); }, []);

  const loadOverview = async () => {
    try {
      const res = await api.get('/api/security/compliance/overview');
      setData(res.data);
    } catch { toast.error('Failed to load compliance overview'); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="flex items-center gap-2 p-8 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" />Loading...</div>;

  const score = data?.score || 0;
  const scoreColor = score >= 80 ? 'text-emerald-400' : score >= 50 ? 'text-amber-400' : 'text-red-400';

  return (
    <div className="space-y-6" data-testid="compliance-overview">
      {/* Score */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-6">
            <div className="relative h-24 w-24">
              <svg viewBox="0 0 100 100" className="transform -rotate-90">
                <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" strokeWidth="6" className="text-muted/30" />
                <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" strokeWidth="6" strokeDasharray={`${score * 2.64} 264`} className={scoreColor} strokeLinecap="round" />
              </svg>
              <span className={`absolute inset-0 flex items-center justify-center text-2xl font-bold ${scoreColor}`}>{score}%</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold">Compliance Score</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {score >= 80 ? 'Good standing. Keep it up!' : score >= 50 ? 'Moderate risk. Address the items below.' : 'Action needed. Several compliance items require attention.'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Checks */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(data?.checks || []).map((check, i) => (
          <Card key={i}>
            <CardContent className="p-4 flex items-center gap-3">
              {check.passed
                ? <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
                : <XCircle className="h-5 w-5 text-red-400 shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{check.name}</p>
                <Badge variant="outline" className={`text-[10px] mt-1 ${check.severity === 'high' ? 'border-red-500/30 text-red-400' : 'border-amber-500/30 text-amber-400'}`}>
                  {check.severity} priority
                </Badge>
              </div>
              <span className={`text-xs ${check.passed ? 'text-emerald-400' : 'text-red-400'}`}>{check.passed ? 'Passed' : 'Not configured'}</span>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold">{data?.audit_events || 0}</p>
          <p className="text-xs text-muted-foreground">Audit Events</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold">{data?.sso_enabled ? 'Active' : 'Off'}</p>
          <p className="text-xs text-muted-foreground">SSO Status</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold">{data?.mfa_enabled ? 'Active' : 'Off'}</p>
          <p className="text-xs text-muted-foreground">MFA Status</p>
        </CardContent></Card>
      </div>
    </div>
  );
}

function SSOConfig() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [form, setForm] = useState({ provider_name: 'Okta', provider_type: 'saml', entity_id: '', sso_url: '', certificate: '', metadata_url: '', enabled: false, enforce_sso: false, allowed_domains: [] });

  useEffect(() => { loadConfig(); }, []);

  const loadConfig = async () => {
    try {
      const res = await api.get('/api/security/sso/config');
      if (res.data?.configured && res.data?.provider) {
        setForm(prev => ({ ...prev, ...res.data.provider }));
      }
      setConfig(res.data);
    } catch {} finally { setLoading(false); }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put('/api/security/sso/config', form);
      toast.success('SSO configuration saved');
      loadConfig();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const res = await api.post('/api/security/sso/test');
      if (res.data?.success) toast.success(res.data.message);
      else toast.error(res.data.message || 'Test failed');
    } catch (err) { toast.error(err.response?.data?.detail || 'Test failed'); }
    finally { setTesting(false); }
  };

  if (loading) return <div className="flex items-center gap-2 p-8 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" />Loading...</div>;

  return (
    <div className="space-y-6" data-testid="sso-config">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">SSO / SAML Provider</CardTitle>
          <CardDescription>Configure single sign-on for your organization</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Provider</Label>
              <Select value={form.provider_name} onValueChange={v => setForm(p => ({ ...p, provider_name: v }))}>
                <SelectTrigger data-testid="sso-provider-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Okta">Okta</SelectItem>
                  <SelectItem value="Azure AD">Azure AD</SelectItem>
                  <SelectItem value="Google Workspace">Google Workspace</SelectItem>
                  <SelectItem value="Custom">Custom SAML</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Protocol</Label>
              <Select value={form.provider_type} onValueChange={v => setForm(p => ({ ...p, provider_type: v }))}>
                <SelectTrigger data-testid="sso-protocol-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="saml">SAML 2.0</SelectItem>
                  <SelectItem value="oidc">OpenID Connect</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Entity ID / Issuer</Label>
            <Input data-testid="sso-entity-id" value={form.entity_id} onChange={e => setForm(p => ({ ...p, entity_id: e.target.value }))} placeholder="https://sso.youridp.com/entity-id" />
          </div>
          <div>
            <Label>SSO Login URL</Label>
            <Input data-testid="sso-url" value={form.sso_url} onChange={e => setForm(p => ({ ...p, sso_url: e.target.value }))} placeholder="https://sso.youridp.com/saml/login" />
          </div>
          <div>
            <Label>Metadata URL (optional)</Label>
            <Input data-testid="sso-metadata-url" value={form.metadata_url} onChange={e => setForm(p => ({ ...p, metadata_url: e.target.value }))} placeholder="https://sso.youridp.com/.well-known/metadata" />
          </div>
          <div>
            <Label>X.509 Certificate</Label>
            <Textarea data-testid="sso-certificate" value={form.certificate} onChange={e => setForm(p => ({ ...p, certificate: e.target.value }))} placeholder="Paste your IdP's X.509 certificate" rows={3} className="font-mono text-xs" />
          </div>
          <div>
            <Label>Allowed Email Domains (comma-separated)</Label>
            <Input data-testid="sso-domains" value={(form.allowed_domains || []).join(', ')} onChange={e => setForm(p => ({ ...p, allowed_domains: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))} placeholder="company.com, team.company.com" />
          </div>

          <div className="flex items-center justify-between border-t border-border/40 pt-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch data-testid="sso-enabled-toggle" checked={form.enabled} onCheckedChange={v => setForm(p => ({ ...p, enabled: v }))} />
                <Label className="text-sm">Enable SSO</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch data-testid="sso-enforce-toggle" checked={form.enforce_sso} onCheckedChange={v => setForm(p => ({ ...p, enforce_sso: v }))} />
                <Label className="text-sm text-muted-foreground">Enforce (disable password login)</Label>
              </div>
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button onClick={saveConfig} disabled={saving} data-testid="sso-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}{saving ? 'Saving...' : 'Save Configuration'}
            </Button>
            <Button variant="outline" onClick={testConnection} disabled={testing || !form.enabled} data-testid="sso-test-btn">
              {testing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
              Test Connection
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">Note: Full SAML handshake requires production IdP setup. Currently in simulation mode.</p>
        </CardContent>
      </Card>
    </div>
  );
}

function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({ entity_type: '', action: '' });

  useEffect(() => { loadLogs(); loadSummary(); }, [filter]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '50' });
      if (filter.entity_type) params.set('entity_type', filter.entity_type);
      if (filter.action) params.set('action', filter.action);
      const res = await api.get(`/api/security/audit-logs?${params}`);
      setLogs(res.data?.logs || []);
      setTotal(res.data?.total || 0);
    } catch { setLogs([]); } finally { setLoading(false); }
  };

  const loadSummary = async () => {
    try {
      const res = await api.get('/api/security/audit-logs/summary');
      setSummary(res.data);
    } catch {}
  };

  return (
    <div className="space-y-6" data-testid="audit-logs">
      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-3 gap-4">
          <Card><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{summary.total_events}</p>
            <p className="text-xs text-muted-foreground">Total Events</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{summary.last_24h}</p>
            <p className="text-xs text-muted-foreground">Last 24h</p>
          </CardContent></Card>
          <Card><CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{summary.last_7d}</p>
            <p className="text-xs text-muted-foreground">Last 7 Days</p>
          </CardContent></Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3">
        <Select value={filter.entity_type || 'all'} onValueChange={v => setFilter(p => ({ ...p, entity_type: v === 'all' ? '' : v }))}>
          <SelectTrigger className="w-40" data-testid="audit-filter-entity"><SelectValue placeholder="Entity type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Entities</SelectItem>
            <SelectItem value="deal">Deals</SelectItem>
            <SelectItem value="lead">Leads</SelectItem>
            <SelectItem value="quote">Quotes</SelectItem>
            <SelectItem value="invoice">Invoices</SelectItem>
            <SelectItem value="client">Clients</SelectItem>
            <SelectItem value="catalog_item">Catalog Items</SelectItem>
            <SelectItem value="session">Sessions</SelectItem>
            <SelectItem value="sso">SSO</SelectItem>
            <SelectItem value="settings">Settings</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={loadLogs}><RefreshCw className="h-4 w-4 mr-1" />Refresh</Button>
      </div>

      {/* Logs table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />Loading audit logs...</div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <ScrollText className="h-8 w-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No audit logs yet</p>
              <p className="text-xs mt-1">Events will appear here as users interact with the CRM</p>
            </div>
          ) : (
            <div className="divide-y divide-border/40">
              {logs.map((log) => (
                <div key={log.id} className="px-4 py-3 text-sm flex items-center gap-3" data-testid={`audit-log-${log.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{log.action?.replace(/_/g, ' ')}</p>
                    <p className="text-xs text-muted-foreground">
                      {log.entity_type} {log.entity_id ? `· ${log.entity_id.slice(0, 8)}...` : ''}
                      {log.user_email ? ` · by ${log.user_email}` : ''}
                    </p>
                  </div>
                  {log.details && Object.keys(log.details).length > 0 && (
                    <Badge variant="outline" className="text-[10px]">
                      {Object.entries(log.details).slice(0, 2).map(([k, v]) => `${k}: ${v}`).join(', ')}
                    </Badge>
                  )}
                  <span className="text-xs text-muted-foreground shrink-0">
                    {log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <p className="text-xs text-muted-foreground">Total: {total} audit events · Immutable and tamper-proof</p>
    </div>
  );
}

function SessionManagement() {
  const [sessions, setSessions] = useState([]);
  const [policy, setPolicy] = useState({ session_timeout_minutes: 480, max_concurrent_sessions: 5, require_mfa: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadSessions(); loadPolicy(); }, []);

  const loadSessions = async () => {
    try {
      const res = await api.get('/api/security/sessions/active');
      setSessions(res.data?.sessions || []);
    } catch {} finally { setLoading(false); }
  };

  const loadPolicy = async () => {
    try {
      const res = await api.get('/api/security/sessions/policy');
      setPolicy(res.data);
    } catch {}
  };

  const savePolicy = async () => {
    setSaving(true);
    try {
      await api.put('/api/security/sessions/policy', policy);
      toast.success('Session policy updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  const terminateSession = async (sessionId) => {
    try {
      await api.delete(`/api/security/sessions/${sessionId}`);
      toast.success('Session terminated');
      loadSessions();
    } catch { toast.error('Failed to terminate session'); }
  };

  const terminateAll = async () => {
    try {
      const res = await api.delete('/api/security/sessions');
      toast.success(`${res.data?.terminated || 0} sessions terminated`);
      loadSessions();
    } catch { toast.error('Failed to terminate sessions'); }
  };

  return (
    <div className="space-y-6" data-testid="session-management">
      {/* Session Policy */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Session Policy</CardTitle>
          <CardDescription>Configure session timeouts and security requirements</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Session Timeout (minutes)</Label>
              <Input type="number" data-testid="session-timeout-input" value={policy.session_timeout_minutes} onChange={e => setPolicy(p => ({ ...p, session_timeout_minutes: parseInt(e.target.value) || 480 }))} />
              <p className="text-xs text-muted-foreground mt-1">{Math.round(policy.session_timeout_minutes / 60)} hours</p>
            </div>
            <div>
              <Label>Max Concurrent Sessions</Label>
              <Input type="number" data-testid="max-sessions-input" value={policy.max_concurrent_sessions} onChange={e => setPolicy(p => ({ ...p, max_concurrent_sessions: parseInt(e.target.value) || 5 }))} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch data-testid="mfa-toggle" checked={policy.require_mfa} onCheckedChange={v => setPolicy(p => ({ ...p, require_mfa: v }))} />
            <Label>Require MFA for all users</Label>
          </div>
          <Button onClick={savePolicy} disabled={saving} data-testid="save-policy-btn">
            {saving ? 'Saving...' : 'Save Policy'}
          </Button>
        </CardContent>
      </Card>

      {/* Active Sessions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div><CardTitle className="text-lg">Active Sessions</CardTitle><CardDescription>{sessions.length} active</CardDescription></div>
            <Button variant="outline" size="sm" onClick={terminateAll} data-testid="terminate-all-btn">
              <Trash2 className="h-4 w-4 mr-1" />Terminate All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center text-muted-foreground p-4"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
          ) : sessions.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No active sessions found</p>
          ) : (
            <div className="space-y-2">
              {sessions.map((s) => {
                const DeviceIcon = s.device_type === 'mobile' ? Smartphone : s.device_type === 'tablet' ? Smartphone : Laptop;
                return (
                <div key={s.session_id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg" data-testid={`session-${s.session_id}`}>
                  <div className="flex items-center gap-3">
                    <DeviceIcon className="h-5 w-5 text-muted-foreground shrink-0" />
                    <div>
                      <p className="text-sm font-medium capitalize">{s.device_type || 'desktop'} session</p>
                      <p className="text-xs text-muted-foreground">
                        <Globe className="h-3 w-3 inline mr-1" />{s.ip_address || 'Unknown IP'} · Last active: {s.last_activity ? new Date(s.last_activity).toLocaleString() : 'N/A'}
                      </p>
                      <p className="text-[10px] text-muted-foreground/70 truncate max-w-xs">{s.user_agent || 'Unknown agent'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {s.remaining_minutes != null && (
                      <Badge variant="outline" className={`text-[10px] ${s.is_expired ? 'text-red-400 border-red-500/30' : s.remaining_minutes < 60 ? 'text-amber-400 border-amber-500/30' : 'text-emerald-400 border-emerald-500/30'}`}>
                        {s.is_expired ? 'Expired' : `${Math.floor(s.remaining_minutes / 60)}h ${s.remaining_minutes % 60}m left`}
                      </Badge>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => terminateSession(s.session_id)} data-testid={`terminate-session-${s.session_id}`}>
                      <XCircle className="h-4 w-4 text-red-400" />
                    </Button>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
